import { Head, router } from '@inertiajs/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '@/layouts/Layout';
import { Heading } from '@/components/ui/heading';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { FlashBanner } from '@/components/ui/flash-banner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { CircularProgress } from '@/components/ui/circular-progress';
import { logError } from '@/lib/logger';

type Subscription = {
  id: number;
  planCode: string;
  planVersion: number;
  displayName: string;
  billingInterval: 'month' | 'year';
  clientLimit: number | null;
  priceCents: number;
  currency: string;
  status: 'pending_payment' | 'active' | 'past_due' | 'expired' | 'canceled';
  source: 'stripe' | 'manual';
  createdAt?: string | null;
  validFrom?: string | null;
  validUntil?: string | null;
  pastDueStartedAt?: string | null;
  pastDueGraceEndsAt?: string | null;
  pendingExpiresAt?: string | null;
  canceledAt?: string | null;
  expiredAt?: string | null;
  checkoutUrl?: string | null;
};

type SubscriptionPlan = {
  id: number;
  planCode: string;
  planVersion: number;
  displayName: string;
  billingInterval: 'month' | 'year';
  clientLimit: number | null;
  priceCents: number;
  currency: string;
  recommended: boolean;
};

type SubscriptionChange = {
  id: number;
  kind: 'create' | 'upgrade' | 'downgrade' | 'cancel';
  status: 'requested' | 'pending_payment' | 'scheduled' | 'applied' | 'failed' | 'canceled' | 'superseded';
  requestedAt?: string | null;
  scheduledFor?: string | null;
  appliedAt?: string | null;
  failedAt?: string | null;
  canceledAt?: string | null;
  failureReason?: string | null;
  fromPlan?: SubscriptionPlan | null;
  toPlan?: SubscriptionPlan | null;
};

type UpgradePreview = {
  amountDueCents: number;
  currency: string;
  prorationCents: number;
  creditCents: number;
  nextPeriodAmountCents: number;
  nextPeriodEnd?: string | null;
  lineItems?: Array<{
    description?: string | null;
    amountCents: number;
    proration?: boolean | null;
  }>;
};

type SubscriptionShowProps = {
  subscription: Subscription | null;
  openChange?: SubscriptionChange | null;
  clientCount: number;
  clientLimit: number | null;
  limitReached: boolean;
  availablePlans: SubscriptionPlan[];
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const formatPrice = (priceCents: number) => {
  if (priceCents === 0) return 'Gratis';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(priceCents / 100);
};

const formatMoney = (priceCents: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(priceCents / 100);

const formatInterval = (interval: Subscription['billingInterval']) =>
  interval === 'month' ? 'Mensile' : 'Annuale';

type Interval = SubscriptionPlan['billingInterval'];

const getIntervalLabel = (interval: Interval) => (interval === 'month' ? 'Mensile' : 'Annuale');

const isFuture = (value?: string | null) => (value ? new Date(value) > new Date() : false);

export default function SubscriptionShow({
  subscription,
  openChange,
  clientCount,
  clientLimit,
  limitReached,
  availablePlans,
}: SubscriptionShowProps) {
  const [isResending, setIsResending] = useState(false);
  const [resendFeedback, setResendFeedback] = useState<string | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<Interval>('month');
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [isManageMode, setIsManageMode] = useState(false);
  const [isDowngradeCanceling, setIsDowngradeCanceling] = useState(false);
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [upgradePreview, setUpgradePreview] = useState<UpgradePreview | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const limitPercent = useMemo(() => {
    if (!clientLimit || clientLimit <= 0) return null;
    return Math.min(100, Math.round((clientCount / clientLimit) * 100));
  }, [clientCount, clientLimit]);

  const badge = useMemo(() => {
    if (!subscription) return null;
    switch (subscription.status) {
      case 'active':
        return { label: 'Attivo', variant: 'default' as const };
      case 'pending_payment':
        return { label: 'In attesa pagamento', variant: 'secondary' as const };
      case 'past_due':
        return { label: 'Past due', variant: 'destructive' as const };
      case 'expired':
        return { label: 'Scaduto', variant: 'outline' as const };
      case 'canceled':
        return { label: 'Cancellato', variant: 'outline' as const };
      default:
        return null;
    }
  }, [subscription]);

  const periodInfo = useMemo(() => {
    if (!subscription) {
      return { label: 'Periodo', value: 'Periodo non disponibile' };
    }

    const hasEnd = Boolean(subscription.validUntil);
    const isManual = subscription.source === 'manual';
    const endLabel = subscription.validUntil ? formatDate(subscription.validUntil) : '—';

    if (subscription.status === 'canceled' && hasEnd) {
      return { label: 'Accesso fino al', value: endLabel };
    }

    if (subscription.status === 'expired' && hasEnd) {
      return { label: 'Scaduto il', value: endLabel };
    }

    if (subscription.status === 'active' && isManual && !hasEnd) {
      return { label: 'Scadenza', value: 'Nessuna scadenza' };
    }

    if (subscription.status === 'active' && hasEnd) {
      return { label: 'Prossimo rinnovo', value: endLabel };
    }

    return { label: 'Periodo', value: 'Periodo non disponibile' };
  }, [subscription]);

  const hasActiveAccess = useMemo(() => {
    if (!subscription) return false;
    if (subscription.status === 'active') return true;
    if (subscription.status === 'canceled') return isFuture(subscription.validUntil);
    if (subscription.status === 'past_due') return isFuture(subscription.pastDueGraceEndsAt);
    return false;
  }, [subscription]);

  const intervalOptions = useMemo<Interval[]>(() => {
    const intervals = Array.from(new Set(availablePlans.map((plan) => plan.billingInterval)));
    return intervals.sort();
  }, [availablePlans]);

  useEffect(() => {
    if (intervalOptions.length === 0) return;
    if (!intervalOptions.includes(selectedInterval)) {
      setSelectedInterval(intervalOptions[0]);
    }
  }, [intervalOptions, selectedInterval]);

  const hasOpenChange = Boolean(openChange);
  const canOverrideDowngrade = useMemo(
    () => openChange?.kind === 'downgrade' && openChange.status === 'scheduled',
    [openChange],
  );
  const canCreateSubscription = useMemo(() => {
    if (!subscription) return true;
    if (subscription.status === 'expired') return true;
    if (subscription.status === 'canceled' && !isFuture(subscription.validUntil)) return true;
    return false;
  }, [subscription]);

  const canChangePlan = useMemo(() => {
    if (!subscription || subscription.source !== 'stripe') return false;
    const activeWithAccess =
      subscription.status === 'active' ||
      (subscription.status === 'canceled' && isFuture(subscription.validUntil));
    if (!activeWithAccess) return false;
    if (!hasOpenChange) return true;
    return canOverrideDowngrade;
  }, [subscription, hasOpenChange, canOverrideDowngrade]);

  useEffect(() => {
    if (!subscription || canCreateSubscription) return;
    if (!canChangePlan && subscription.billingInterval !== selectedInterval) {
      setSelectedInterval(subscription.billingInterval);
    }
  }, [subscription, canCreateSubscription, canChangePlan, selectedInterval]);

  const filteredPlans = useMemo(
    () => availablePlans.filter((plan) => plan.billingInterval === selectedInterval),
    [availablePlans, selectedInterval],
  );

  const recommendedPlanId = useMemo(() => {
    const explicit = filteredPlans.find((plan) => plan.recommended);
    if (explicit) return explicit.id;
    if (filteredPlans.length === 3) {
      return filteredPlans[1]?.id ?? null;
    }
    return null;
  }, [filteredPlans]);

  const displayPlans = useMemo(
    () =>
      filteredPlans.map((plan) => ({
        ...plan,
        isRecommended: recommendedPlanId === plan.id,
      })),
    [filteredPlans, recommendedPlanId],
  );

  useEffect(() => {
    if (!filteredPlans.find((plan) => plan.id === selectedPlanId)) {
      setSelectedPlanId(null);
    }
  }, [filteredPlans, selectedPlanId]);

  const selectedPlan = useMemo(
    () => filteredPlans.find((plan) => plan.id === selectedPlanId) || null,
    [filteredPlans, selectedPlanId],
  );

  const selectedAction = useMemo(() => {
    if (!selectedPlan) return null;
    if (canCreateSubscription) return 'create';
    if (!subscription || !canChangePlan) return null;
    if (selectedPlan.priceCents > subscription.priceCents) return 'upgrade';
    if (selectedPlan.priceCents < subscription.priceCents) return 'downgrade';
    return null;
  }, [selectedPlan, canCreateSubscription, canChangePlan, subscription]);

  const fetchUpgradePreview = useCallback(async () => {
    if (!selectedPlan || selectedAction !== 'upgrade' || !subscription) return;

    setIsPreviewLoading(true);
    setPreviewError(null);
    setUpgradePreview(null);

    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      const response = await fetch('/therapists/subscription/upgrade_preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        credentials: 'same-origin',
        body: JSON.stringify({ plan_id: selectedPlan.id, override: canOverrideDowngrade }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 409) {
          setPreviewError('Esiste già una richiesta di cambio in corso.');
        } else {
          setPreviewError('Impossibile calcolare il costo dell’upgrade. Riprova tra poco.');
        }
        return;
      }

      setUpgradePreview(payload as UpgradePreview);
    } catch (error) {
      logError('Errore preview upgrade abbonamento', error);
      setPreviewError('Impossibile calcolare il costo dell’upgrade. Riprova tra poco.');
    } finally {
      setIsPreviewLoading(false);
    }
  }, [selectedPlan, selectedAction, subscription, canOverrideDowngrade]);

  useEffect(() => {
    if (!isUpgradeDialogOpen) return;
    void fetchUpgradePreview();
  }, [isUpgradeDialogOpen, fetchUpgradePreview]);

  const actionsLocked = useMemo(() => {
    if (hasOpenChange && !canOverrideDowngrade && !canCreateSubscription) return true;
    if (canCreateSubscription) return false;
    return !(subscription && subscription.status === 'active' && subscription.source === 'stripe');
  }, [hasOpenChange, canOverrideDowngrade, canCreateSubscription, subscription]);

  const upgradeReady = useMemo(() => {
    if (selectedAction !== 'upgrade') return true;
    return Boolean(upgradePreview) && !previewError && !isPreviewLoading;
  }, [selectedAction, upgradePreview, previewError, isPreviewLoading]);

  const previewLineItems = useMemo(() => {
    if (!upgradePreview?.lineItems?.length) return [];
    return upgradePreview.lineItems.filter((item) => item.amountCents !== 0);
  }, [upgradePreview]);

  const canCancel = useMemo(() => {
    return subscription?.status === 'active' && subscription.source === 'stripe';
  }, [subscription]);

  const annualSavings = useMemo(() => {
    const monthlyPlans = availablePlans.filter((plan) => plan.billingInterval === 'month');
    const annualPlans = availablePlans.filter((plan) => plan.billingInterval === 'year');
    if (!monthlyPlans.length || !annualPlans.length) return null;

    const savings = annualPlans
      .map((annualPlan) => {
        const match = monthlyPlans.find((monthlyPlan) => monthlyPlan.clientLimit === annualPlan.clientLimit);
        if (!match) return null;
        const monthlyCost = match.priceCents * 12;
        if (monthlyCost <= 0) return null;
        return Math.round((1 - annualPlan.priceCents / monthlyCost) * 100);
      })
      .filter((value): value is number => value !== null && value > 0);

    return savings.length ? Math.max(...savings) : null;
  }, [availablePlans]);

  const openChangeMessage = useMemo(() => {
    if (!openChange) return null;
    if (canCreateSubscription) return null;
    if (openChange.kind === 'downgrade' && openChange.status === 'scheduled') {
      const planName = openChange.toPlan?.displayName || 'nuovo piano';
      const dateLabel = formatDate(openChange.scheduledFor);
      return `Downgrade programmato al ${dateLabel} verso ${planName}.`;
    }
    if (openChange.status === 'pending_payment') {
      return 'Richiesta in attesa del completamento del pagamento.';
    }
    if (openChange.status === 'requested') {
      return 'Richiesta in elaborazione. Ti aggiorniamo a breve.';
    }
    return 'Richiesta di cambio in corso.';
  }, [openChange, canCreateSubscription]);

  const handleResend = async () => {
    if (isResending) return;
    setIsResending(true);
    setResendFeedback(null);
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      const response = await fetch('/therapists/subscription/resend_payment_link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        credentials: 'same-origin',
      });

      if (response.ok) {
        setResendFeedback('Link inviato via email.');
      } else {
        setResendFeedback('Impossibile inviare il link. Riprova più tardi.');
      }
    } catch (error) {
      logError('Errore reinvio link pagamento', error);
      setResendFeedback('Impossibile inviare il link. Riprova più tardi.');
    } finally {
      setIsResending(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedPlan || isCheckingOut || actionsLocked || selectedAction !== 'create') return;
    setIsCheckingOut(true);
    setCheckoutError(null);

    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      const response = await fetch('/therapists/subscription/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        credentials: 'same-origin',
        body: JSON.stringify({ plan_id: selectedPlan.id, billing_interval: selectedPlan.billingInterval }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 409) {
          setCheckoutError('Esiste già una richiesta di cambio in corso.');
        } else {
          setCheckoutError('Impossibile avviare il pagamento. Riprova più tardi.');
        }
        return;
      }

      if (payload.checkout_url) {
        window.location.assign(payload.checkout_url);
      } else {
        setCheckoutError('Checkout non disponibile. Riprova più tardi.');
      }
    } catch (error) {
      logError('Errore checkout self-serve', error);
      setCheckoutError('Impossibile avviare il pagamento. Riprova più tardi.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleUpgrade = async (override = false) => {
    if (!selectedPlan || isCheckingOut || selectedAction !== 'upgrade') return;
    if (!upgradeReady) {
      setActionError('Calcolo importo non disponibile. Riprova tra poco.');
      return;
    }
    setIsCheckingOut(true);
    setActionError(null);

    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      const response = await fetch('/therapists/subscription/upgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        credentials: 'same-origin',
        body: JSON.stringify({ plan_id: selectedPlan.id, override }),
      });

      if (!response.ok) {
        setActionError('Impossibile completare l’upgrade. Riprova più tardi.');
        return;
      }

      router.reload();
    } catch (error) {
      logError('Errore upgrade abbonamento', error);
      setActionError('Impossibile completare l’upgrade. Riprova più tardi.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleDowngrade = async () => {
    if (!selectedPlan || isCheckingOut || selectedAction !== 'downgrade') return;
    setIsCheckingOut(true);
    setActionError(null);

    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      const response = await fetch('/therapists/subscription/downgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        credentials: 'same-origin',
        body: JSON.stringify({ plan_id: selectedPlan.id }),
      });

      if (!response.ok) {
        setActionError('Impossibile programmare il downgrade. Riprova più tardi.');
        return;
      }

      router.reload();
    } catch (error) {
      logError('Errore downgrade abbonamento', error);
      setActionError('Impossibile programmare il downgrade. Riprova più tardi.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleCancelDowngrade = async () => {
    if (!openChange || openChange.kind !== 'downgrade' || isDowngradeCanceling) return;
    setIsDowngradeCanceling(true);
    setActionError(null);

    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      const response = await fetch('/therapists/subscription/cancel_downgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        setActionError('Impossibile annullare il downgrade. Riprova più tardi.');
        return;
      }

      router.reload();
    } catch (error) {
      logError('Errore annullo downgrade', error);
      setActionError('Impossibile annullare il downgrade. Riprova più tardi.');
    } finally {
      setIsDowngradeCanceling(false);
    }
  };

  const handleCancel = async () => {
    if (!canCancel || isCanceling) return;
    setIsCanceling(true);
    setCancelError(null);

    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      const response = await fetch('/therapists/subscription/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        setCancelError("Impossibile cancellare l'abbonamento. Riprova più tardi.");
        return;
      }

      setIsCancelOpen(false);
      router.reload();
    } catch (error) {
      logError('Errore cancellazione abbonamento', error);
      setCancelError("Impossibile cancellare l'abbonamento. Riprova più tardi.");
    } finally {
      setIsCanceling(false);
    }
  };

  const handlePlanAction = () => {
    if (!selectedAction) return;
    if (selectedAction === 'create') {
      void handleCheckout();
      return;
    }
    if (selectedAction === 'upgrade') {
      setActionError(null);
      setIsUpgradeDialogOpen(true);
      return;
    }
    if (selectedAction === 'downgrade') {
      void handleDowngrade();
    }
  };

  const handleScrollToPlans = () => {
    setIsManageMode(true);
    window.setTimeout(() => {
      const target = document.getElementById('subscription-plans');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  };

  return (
    <Layout
      isAdmin
      hideSubscriptionBanner
      mainHeader={
        <Heading
          level={2}
          description="Stato e dettagli del tuo abbonamento professionale."
          wrapperClassName="mb-4"
        >
          Abbonamento
        </Heading>
      }
    >
      <Head title="Abbonamento" />

      <div className="space-y-6">
        {subscription ? (
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader className="flex flex-wrap items-start justify-between gap-4">
                <CardTitle>Il tuo piano: {subscription.displayName}</CardTitle>
                {badge ? <Badge variant={badge.variant}>{badge.label}</Badge> : null}
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <CardDescription>Fatturazione</CardDescription>
                  <p>
                    {formatInterval(subscription.billingInterval)} · {formatPrice(subscription.priceCents)}
                  </p>
                </div>
                <div className="space-y-1">
                  <CardDescription>{periodInfo.label}</CardDescription>
                  <p>{periodInfo.value}</p>
                </div>
              </CardContent>
              <CardFooter className="flex flex-wrap gap-2">
                {subscription.status === 'pending_payment' ? (
                  subscription.checkoutUrl ? (
                    <Button asChild>
                      <a href={subscription.checkoutUrl} target="_blank" rel="noreferrer" aria-label="Completa pagamento">
                        Completa pagamento
                      </a>
                    </Button>
                  ) : (
                    <Button onClick={handleResend} disabled={isResending} aria-label="Reinvia link pagamento">
                      {isResending ? 'Invio...' : 'Reinvia link'}
                    </Button>
                  )
                ) : null}
                {(canCancel || canChangePlan) && subscription.status !== 'pending_payment' ? (
                  <Dialog open={isManageOpen} onOpenChange={setIsManageOpen}>
                    <Button variant="outline" onClick={() => setIsManageOpen(true)} aria-label="Gestisci abbonamento">
                      Gestisci
                    </Button>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Gestisci abbonamento</DialogTitle>
                        <DialogDescription>Seleziona l&apos;azione che vuoi eseguire.</DialogDescription>
                      </DialogHeader>
                      <DialogFooter className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsManageOpen(false);
                            handleScrollToPlans();
                          }}
                        >
                          Cambia piano
                        </Button>
                        {canCancel ? (
                          <Button
                            variant="destructive"
                            onClick={() => {
                              setIsManageOpen(false);
                              setIsCancelOpen(true);
                            }}
                          >
                            Cancella abbonamento
                          </Button>
                        ) : null}
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                ) : null}
                {resendFeedback ? <CardDescription>{resendFeedback}</CardDescription> : null}
              </CardFooter>
            </Card>
            {hasActiveAccess ? (
            <Card semantic={limitReached ? 'warning' : 'none'}>
              <CardHeader>
                <CardTitle>Limite clienti</CardTitle>
              </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-4">
                      {limitPercent !== null ? (
                        <CircularProgress value={limitPercent} label="Clienti" />
                      ) : (
                        <Badge variant="secondary">Illimitato</Badge>
                      )}
                      <div className="space-y-1">
                        <p>{clientLimit ? `${clientCount} / ${clientLimit}` : `${clientCount} clienti`}</p>
                        {limitReached ? (
                          <CardDescription>
                            Hai raggiunto il limite. Non puoi invitare nuovi clienti finché non liberi posti.
                          </CardDescription>
                        ) : (
                          <CardDescription>Puoi ancora invitare nuovi clienti.</CardDescription>
                        )}
                      </div>
                    </div>
                    {limitReached ? <Badge variant="destructive">Limite raggiunto</Badge> : null}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card variant="outlined">
                <CardHeader>
                  <CardTitle>Abbonamento non attivo</CardTitle>
                  <CardDescription>
                    Il limite clienti è disponibile solo con un abbonamento attivo.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <CardDescription>Seleziona un piano per riattivare l’accesso.</CardDescription>
                </CardContent>
              </Card>
            )}
          </div>
        ) : null}

        <Dialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancellare l&apos;abbonamento?</DialogTitle>
              <DialogDescription>
                Continui ad avere accesso fino al termine del periodo pagato. Il rinnovo automatico verrà disattivato.
              </DialogDescription>
            </DialogHeader>
            {cancelError ? <FlashBanner tone="error" message={cancelError} /> : null}
            <DialogFooter className="gap-2">
              <DialogClose asChild>
                <Button variant="outline">Annulla</Button>
              </DialogClose>
              <Button variant="destructive" onClick={handleCancel} disabled={isCanceling}>
                {isCanceling ? 'Cancellazione...' : 'Conferma'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {openChangeMessage ? (
          <Card variant="outlined">
            <CardHeader>
              <CardTitle>Richiesta in corso</CardTitle>
              <CardDescription>{openChangeMessage}</CardDescription>
            </CardHeader>
            {openChange?.kind === 'downgrade' && openChange.status === 'scheduled' ? (
              <CardContent className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <CardDescription>Puoi annullare il downgrade prima della data di rinnovo.</CardDescription>
                <Button
                  variant="outline"
                  onClick={handleCancelDowngrade}
                  disabled={isDowngradeCanceling}
                  aria-label="Annulla downgrade"
                >
                  {isDowngradeCanceling ? 'Annullamento...' : 'Annulla downgrade'}
                </Button>
              </CardContent>
            ) : null}
          </Card>
        ) : null}

        {(canCreateSubscription || isManageMode) && (
          <Card id="subscription-plans">
            <CardHeader>
              <CardTitle>Seleziona un piano</CardTitle>
              <CardDescription>Scegli la frequenza e il piano più adatto al tuo studio.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {intervalOptions.length ? (
                <ToggleGroup
                  type="single"
                  value={selectedInterval}
                  onValueChange={(value) => value && setSelectedInterval(value as Interval)}
                >
                  {intervalOptions.map((interval) => (
                    <ToggleGroupItem
                      key={interval}
                      value={interval}
                      aria-label={`Piani ${getIntervalLabel(interval)}`}
                      disabled={!canCreateSubscription && !canChangePlan}
                    >
                      {getIntervalLabel(interval)}
                      {interval === 'year' && annualSavings ? (
                        <Badge variant="secondary" className="ml-2">
                          Risparmi {annualSavings}%
                        </Badge>
                      ) : null}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              ) : null}

              {actionsLocked && !openChangeMessage ? (
                <FlashBanner
                  tone="info"
                  message="Non puoi modificare il piano in questo stato. Completa eventuali pagamenti o attendi la scadenza."
                />
              ) : null}

              {!displayPlans.length ? (
                <Card variant="outlined">
                  <CardHeader>
                    <CardTitle>Nessun piano disponibile</CardTitle>
                    <CardDescription>Contatta l&apos;amministrazione per assistenza.</CardDescription>
                  </CardHeader>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-3">
                  {displayPlans.map((plan) => {
                    const isSelected = plan.id === selectedPlanId;
                    const isDisabled = actionsLocked;
                    const isRecommended = plan.isRecommended;
                    return (
                      <Card
                        key={plan.id}
                        variant={isDisabled ? 'outlined' : isRecommended ? 'elevated' : 'interactive'}
                        semantic={isRecommended ? 'primary' : 'none'}
                        className={
                          isDisabled
                            ? 'opacity-70'
                            : isSelected
                              ? 'border-primary cursor-pointer'
                              : 'cursor-pointer'
                        }
                        aria-disabled={isDisabled}
                        onClick={() => {
                          if (!isDisabled) {
                            setSelectedPlanId(plan.id);
                          }
                        }}
                      >
                        <CardHeader className="space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1">
                              <CardTitle headingLevel="h3">{plan.displayName}</CardTitle>
                              <CardDescription>
                                {formatInterval(plan.billingInterval)} · {formatPrice(plan.priceCents)}
                              </CardDescription>
                            </div>
                            {isRecommended ? <Badge>Consigliato</Badge> : null}
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <CardDescription>
                            {plan.clientLimit ? `${plan.clientLimit} clienti inclusi` : 'Clienti illimitati'}
                          </CardDescription>
                          {isSelected ? <Badge variant="secondary">Selezionato</Badge> : null}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {checkoutError ? <FlashBanner tone="error" message={checkoutError} /> : null}
              {actionError ? <FlashBanner tone="error" message={actionError} /> : null}

              <Dialog
                open={isUpgradeDialogOpen}
                onOpenChange={(open) => {
                  setIsUpgradeDialogOpen(open);
                  if (!open) {
                    setUpgradePreview(null);
                    setPreviewError(null);
                    setIsPreviewLoading(false);
                  }
                }}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Condizioni upgrade</DialogTitle>
                    <DialogDescription>Verifica i costi prima di procedere con l&apos;upgrade.</DialogDescription>
                  </DialogHeader>
                  {canOverrideDowngrade ? (
                    <FlashBanner
                      tone="info"
                      message="Questo annullerà il downgrade programmato e procederà con il pagamento immediato."
                    />
                  ) : null}
                  <Card variant="outlined" isLoading={isPreviewLoading}>
                    <CardHeader>
                      <CardTitle>Riepilogo pagamento</CardTitle>
                      <CardDescription>Importo addebitato oggi.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {previewError ? <FlashBanner tone="error" message={previewError} /> : null}
                      {upgradePreview ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-4">
                            <CardDescription>Totale oggi</CardDescription>
                            <p>{formatMoney(upgradePreview.amountDueCents)}</p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-4">
                              <CardDescription>Pro-rata periodo corrente</CardDescription>
                              <p>{formatMoney(upgradePreview.prorationCents)}</p>
                            </div>
                            <div className="flex items-center justify-between gap-4">
                              <CardDescription>Credito residuo</CardDescription>
                              <p>{formatMoney(upgradePreview.creditCents)}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <CardDescription>Prossimo rinnovo</CardDescription>
                            <p>
                              {formatMoney(upgradePreview.nextPeriodAmountCents)}
                              {upgradePreview.nextPeriodEnd ? ` · ${formatDate(upgradePreview.nextPeriodEnd)}` : ''}
                            </p>
                          </div>
                          {previewLineItems.length ? (
                            <div className="space-y-2">
                              <CardDescription>Dettaglio addebito</CardDescription>
                              <div className="space-y-2">
                                {previewLineItems.map((item, index) => (
                                  <div key={`${item.description ?? 'line'}-${index}`} className="flex items-center justify-between gap-4">
                                    <CardDescription>{item.description || 'Addebito periodo corrente'}</CardDescription>
                                    <p>{formatMoney(item.amountCents)}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <CardDescription>
                              Il totale oggi è calcolato in base ai giorni rimanenti del periodo corrente.
                            </CardDescription>
                          )}
                        </div>
                      ) : null}
                    </CardContent>
                  </Card>
                  {actionError ? <FlashBanner tone="error" message={actionError} /> : null}
                  <DialogFooter className="gap-2">
                    <DialogClose asChild>
                      <Button variant="outline">Annulla</Button>
                    </DialogClose>
                    {previewError ? (
                      <Button variant="outline" onClick={fetchUpgradePreview} disabled={isPreviewLoading}>
                        Riprova
                      </Button>
                    ) : null}
                    <Button
                      onClick={() => {
                        setIsUpgradeDialogOpen(false);
                        void handleUpgrade(canOverrideDowngrade);
                      }}
                      disabled={isCheckingOut || !upgradeReady}
                    >
                      Accetta e paga
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
            <CardFooter className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <CardDescription>
                {selectedPlan
                  ? `${selectedPlan.displayName} · ${formatInterval(selectedPlan.billingInterval)}`
                  : 'Seleziona un piano per continuare.'}
              </CardDescription>
              {selectedAction ? (
                <Button
                  onClick={handlePlanAction}
                  disabled={!selectedPlan || actionsLocked || isCheckingOut}
                  aria-label="Conferma piano"
                >
                  {isCheckingOut
                    ? 'Operazione in corso...'
                    : selectedAction === 'create'
                      ? 'Scegli e paga'
                      : selectedAction === 'upgrade'
                        ? 'Vedi condizioni'
                        : 'Programma downgrade'}
                </Button>
              ) : null}
            </CardFooter>
          </Card>
        )}
      </div>
    </Layout>
  );
}
